import { type Column, Workbook, type Worksheet } from 'exceljs';
import type {
  GridColumnDef,
  GridColumnMeta,
  GridRawRowDataType,
  GridRowDataType,
  GridRowInstance,
  GridStyleGetter,
  HeaderCellToExport,
  MergedCell,
} from '@/common/components/molecules/modernGrid/modernGrid.types';
import type { HeaderGroup, Row, VisibilityState } from '@tanstack/vue-table';
import {
  getHeaderRowSpan,
  getIsDefaultColumn,
} from '@/common/components/molecules/modernGrid/modernGrid.utils';
import type { ExcelColorStyles, ExportExcelOption } from './excel.types';
import { formatRowData, formatDecimal, exportExcelFile, getLevelSymbol } from './excel.utils';
import { GRID_COLUMN_DEFAULT_STYLE, MODERN_GRID_DEPTH_COLUMN } from './excel.define';
import { convertMsToSec, convertTimeToSec } from '@/common/utils/commonUtils';

const isTreeGridRow = <T extends GridRawRowDataType>(
  rows: Readonly<GridRowInstance<T>[]>,
): boolean => {
  if (rows.length === 0) {
    return false;
  }
  // rowDetail의 detail 행(isDetail: true)은 트리 구조 판단에서 제외
  return rows.some((row) => row.depth > 0 && !row.original?.isDetail);
};

export const useTableExport = <RowDataType extends GridRawRowDataType>(
  visibilityState: VisibilityState,
) => {
  const isHiddenColumn = (columnId: string, option: ExportExcelOption<RowDataType>): boolean =>
    (visibilityState?.[columnId] === false ||
      option.hiddenField?.includes(columnId) ||
      getIsDefaultColumn(columnId)) &&
    !option.shownField?.includes(columnId);

  const createHeaders = ({
    gridHeaders,
    headerRowLength,
    worksheet,
    option,
    isTreeGrid,
  }: {
    gridHeaders: Readonly<HeaderGroup<GridRowDataType<RowDataType>>[]>;
    headerRowLength: number;
    worksheet: Worksheet;
    option: ExportExcelOption<RowDataType>;
    isTreeGrid: boolean;
  }) => {
    const headerRows: HeaderCellToExport[][] = Array.from({ length: headerRowLength }, () => []);
    const columnKeys: Partial<Column>[] = [];
    const mergedCells: MergedCell[] = [];

    const convertCaptionObject = (option.convertCaption || []).reduce<Record<string, string>>(
      (acc, cur) => {
        acc[cur.field] = cur.caption;
        return acc;
      },
      {},
    );

    if (isTreeGrid) {
      columnKeys.push(MODERN_GRID_DEPTH_COLUMN);
    }

    gridHeaders.forEach((headerGroup, index) => {
      if (isTreeGrid) {
        headerRows[index].push({
          colSpan: 1,
          rowSpan: index === 0 ? headerRowLength : 0,
          value: '',
          processed: false,
        });
      }

      headerGroup.headers.forEach((header) => {
        const columnId = header.column.id;
        const headerRowIndex = header.depth - 1;
        const optionWidth = option.fieldWidth?.[columnId] ?? (header.getSize() ?? 400) / 6;

        if (isHiddenColumn(columnId, option)) {
          return;
        }

        const meta = header.column.columnDef.meta as GridColumnMeta | undefined;
        const headerValue =
          meta?.attrs?.excelCaption ||
          convertCaptionObject[columnId] ||
          (header.column.columnDef.header as string);

        const headerCell: HeaderCellToExport = {
          value: headerValue,
          colSpan: header.colSpan || 1,
          rowSpan: getHeaderRowSpan(header),
          processed: false,
        };
        headerRows[headerRowIndex].push(headerCell);

        if (headerRowIndex === headerRowLength - 1) {
          columnKeys.push({
            key: columnId,
            width: optionWidth,
            style: GRID_COLUMN_DEFAULT_STYLE,
          });
        }
      });
    });

    headerRows.forEach((row, rowIndex) => {
      let currentCol = 1;

      row.forEach((headerCell) => {
        const isOverlapped = mergedCells.some(
          (mergedCell) =>
            rowIndex + 1 >= mergedCell.rowStart &&
            rowIndex + 1 <= mergedCell.rowEnd &&
            currentCol >= mergedCell.colStart &&
            currentCol <= mergedCell.colEnd,
        );

        if (isOverlapped) {
          currentCol += headerCell.colSpan;
          return;
        }

        const startCol = currentCol;
        const endCol = currentCol + headerCell.colSpan - 1;
        const startRow = rowIndex + 1;
        const endRow = startRow + (headerCell.rowSpan - 1);

        const cell = worksheet.getCell(startRow, startCol);
        cell.value = headerCell.value;

        if (headerCell.colSpan > 1 || headerCell.rowSpan > 1) {
          worksheet.mergeCells(startRow, startCol, endRow, endCol);

          mergedCells.push({
            rowStart: startRow,
            rowEnd: endRow,
            colStart: startCol,
            colEnd: endCol,
          });
        }

        currentCol = endCol + 1;
      });
    });

    worksheet.columns = columnKeys;
  };

  const formatGridDataToExcelData = (
    columns: Readonly<GridColumnDef<RowDataType>[]>,
    gridRows: Readonly<GridRowInstance<RowDataType>[]>,
    option: ExportExcelOption<RowDataType>,
  ) => {
    const { rowDataFormatter, convertSec = false } = option;
    const parentIdStack: string[] = [];
    let isSiblingWithChildrenAppearedBefore: boolean = false;
    return gridRows.reduce<Record<string, string | number | null>[]>(
      (
        acc: Record<string, string | number | null>[],
        cur: GridRowInstance<RowDataType>,
        curIndex: number,
      ) => {
        const row: Record<string, string | number | null> = {};
        const rowData = cur.original;

        // <---- tree grid depth logic
        const hasChildren = cur.subRows.length > 0;
        const { parentId } = cur;
        if (parentId && parentIdStack.includes(parentId)) {
          isSiblingWithChildrenAppearedBefore =
            parentId !== parentIdStack[parentIdStack.length - 1];
          if (isSiblingWithChildrenAppearedBefore) {
            parentIdStack.pop();
          }
        }
        if (hasChildren) {
          parentIdStack.push(cur.id);
        }
        row.level = getLevelSymbol(cur.depth, hasChildren, isSiblingWithChildrenAppearedBefore);
        // tree grid depth logic ---->

        if (rowData.isDetail) {
          return acc;
        }

        columns.forEach((column) => {
          const { id, meta } = column;
          if (!id || isHiddenColumn(id, option)) {
            return;
          }

          if (rowDataFormatter?.[id]) {
            row[id] = rowDataFormatter[id](rowData[id], rowData, curIndex);
          } else if (meta?.rendererType === 'ratio' || meta?.rendererType === 'digit') {
            row[id] = formatDecimal(rowData[id], meta.decimal ?? 3);
          } else {
            const isSecField =
              convertSec && (meta?.rendererType === 'sec' || meta?.rendererType === 'toSec');
            if (isSecField) {
              row[id] =
                meta?.rendererType === 'toSec'
                  ? convertTimeToSec(rowData[id] as number, meta?.attrs?.unit, 'string')
                  : convertMsToSec(rowData[id]);
            } else {
              row[id] = formatRowData(rowData[id], { type: meta?.type });
            }
          }
        });
        acc.push(row);
        return acc;
      },
      [],
    );
  };

  const createExcelFile = ({
    gridHeaders,
    headerRowLength,
    gridRows,
    option,
  }: {
    gridHeaders: Readonly<HeaderGroup<GridRowDataType<RowDataType>>[]>;
    headerRowLength: number;
    gridRows: Readonly<GridRowInstance<RowDataType>[]>;
    option: ExportExcelOption<RowDataType>;
  }) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet();

    const isTreeGrid = isTreeGridRow(gridRows);
    createHeaders({ gridHeaders, headerRowLength, worksheet, option, isTreeGrid });
    const columns = gridHeaders[headerRowLength - 1].headers.map(
      ({ column }) => column.columnDef as GridColumnDef<RowDataType>,
    );
    const data = formatGridDataToExcelData(columns, gridRows, option);
    worksheet.insertRows(headerRowLength + 1, data);

    return workbook;
  };

  const exportToExcelFromClient = async (
    gridHeaders: Readonly<HeaderGroup<GridRowDataType<RowDataType>>[]>,
    gridRows: Readonly<GridRowInstance<RowDataType>[]>,
    option: ExportExcelOption<RowDataType> = {},
  ) => {
    const headerRowLength = Math.max(...gridHeaders.map((group) => group.depth)) + 1;
    const workbook = createExcelFile({ gridHeaders, headerRowLength, gridRows, option });
    await exportExcelFile(workbook, option, headerRowLength);
  };

  const getExcelStylesFromRow = (
    rows: Row<GridRowDataType<RowDataType>>[],
    styleGetter: GridStyleGetter<RowDataType>,
  ): ExcelColorStyles => {
    const { cellStyle, rowStyle } = styleGetter;

    return rows.reduce<ExcelColorStyles>(
      (acc, row) => {
        const { backgroundColor: rowBackgroundColor, '--dynamic-color': rowFontColor } =
          rowStyle(row);

        acc.rowStyle!.push({
          bgColor: rowBackgroundColor,
          fontColor: rowFontColor as string | undefined,
        });

        const cells = row.getAllCells();
        cells.forEach((cell) => {
          const { backgroundColor: cellBackgroundColor, '--dynamic-color': cellFontColor } =
            cellStyle(cell, cells);
          const field = cell.column.id;
          const style = {
            bgColor: cellBackgroundColor,
            fontColor: cellFontColor as string | undefined,
          };

          if (acc.cellStyle[field]) {
            acc.cellStyle[field].push(style);
          } else {
            acc.cellStyle[field] = [style];
          }
        });

        return acc;
      },
      { rowStyle: [], cellStyle: {} },
    );
  };

  return {
    exportToExcelFromClient,
    getExcelStylesFromRow,
  };
};
